import { Navigate, useNavigate, useParams } from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import {
  useGetDealGeneralPartnerViewV3,
  useUpdateDealV2,
} from "@vega-api/generated/deal/deal";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { ActionMenu } from "@vega-core/components/ActionMenu/ActionMenu";
import { type ActionMenuItemProps } from "@vega-core/components/ActionMenu/ActionMenuItem";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { CancelDealModal } from "../components/CancelDealModal";
import {
  type CoInvestmentEditFormData,
  CoInvestmentInlineForm,
} from "../components/CoInvestmentEditForm/CoInvestmentEditForm";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { useCoInvestDealReadOnly } from "../hooks/useCoInvestDealReadOnly";
import { useDealName } from "../hooks/useDealName";
import { transformMetricsForApi } from "../utils/metricTransforms";
import { toMoneyWithZeroDefault } from "../utils/toMoneyWithZeroDefault";

export const DetailsPage = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { featureFlag } = useFeatureFlag();
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];
  const isBookBuildEnabled =
    featureFlag[FeatureFlag.COINVEST_BOOK_BUILD_VD_26153];
  const isReportingEnabled =
    featureFlag[FeatureFlag.COINVEST_REPORTING_VD_26152];

  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId: id },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );
  const canEditDeal = permissions?.canEditDeal;
  const canEditDealOverview = permissions?.canEditDealOverview;

  const {
    isOpen: isCancelDealModalOpen,
    setTrue: openCancelDealModal,
    setFalse: closeCancelDealModal,
  } = useBoolean();

  const { value: isSaving, setValue: setIsSaving } = useBoolean(false);

  const { dealName, isLoading: isDealNameLoading } = useDealName(id);

  const {
    data: dealResponse,
    isLoading,
    error,
    refetch,
  } = useGetDealGeneralPartnerViewV3(id, {
    query: {
      enabled: !!id,
      select: (data) => data.data,
    },
  });
  const updateDealMutation = useUpdateDealV2({
    mutation: {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_updateDeal(),
        });
      },
    },
  });

  const isDealCancelled =
    dealResponse?.deal.status === DealStatusEnum.CANCELLED;

  const { isMutationLocked } = useCoInvestDealReadOnly();
  const canCancelDeal = canEditDeal && !isDealCancelled && !isMutationLocked;

  const actionMenuItems: ActionMenuItemProps[] = [
    ...(canCancelDeal
      ? [
          {
            title: m.coInvestments_cancelDeal_action_button(),
            onClick: openCancelDealModal,
            className: "text-text/negative",
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !dealResponse) {
    return <Navigate replace to={CoInvestmentFullPaths.CoInvestments} />;
  }

  const handleSubmit = async (values: CoInvestmentEditFormData) => {
    if (!id) return;

    const updateData = {
      name: values.name,
      description: values.description,
      assetClass: values.assetClass,
      fundName: values.fundName,
      currency: values.currency,
      ...(isBookBuildEnabled
        ? {
            fundContributionAmount: toMoneyWithZeroDefault(
              values.fundContributionAmount,
              values.currency,
            ),
          }
        : {
            targetAmount: values.targetAmount
              ? { ...values.targetAmount, currency: values.currency }
              : undefined,
          }),
      totalDealSize: toMoneyWithZeroDefault(
        values.totalDealSize,
        values.currency,
      ),
      closingDate: values.closingDate || undefined,
      ...(isReportingEnabled ? { autoClose: values.autoClose } : {}),
      generalPartnerId: dealResponse.deal.generalPartnerId,
      sector: values.sector,
      region: values.region,
      securityType: values.securityType,
      dealOverview: values.dealOverview
        ? {
            ...values.dealOverview,
            metrics: transformMetricsForApi(values.dealOverview.metrics),
          }
        : undefined,
    };

    try {
      setIsSaving(true);
      await updateDealMutation.mutateAsync({
        id,
        data: updateData,
      });
      showToast({
        variant: "success",
        title: m.status_success(),
        description: m.common_saveChangesSuccess(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="my-4 max-w-3xl mx-auto">
        <CoInvestmentPageHeader
          action={
            actionMenuItems.length > 0 ? (
              <ActionMenu menuItems={actionMenuItems} />
            ) : undefined
          }
          backButtonDataTestId="back-to-configure-button"
          fundName={dealName}
          isLoading={isDealNameLoading}
          onBack={
            isNewNavEnabled
              ? () =>
                  navigate(
                    CoInvestmentFullPaths.CoInvestmentConfigure.replace(
                      ":id",
                      id,
                    ),
                  )
              : undefined
          }
          showClosedDealNotice
          showDrawer={!isNewNavEnabled}
          title={m.coInvestments_nav_dealInformation()}
        />

        <div className="mt-6">
          <div className="bg-background/primary">
            <CoInvestmentInlineForm
              canEditDeal={canEditDeal}
              canEditDealOverview={canEditDealOverview}
              dealResponse={dealResponse}
              isLoading={updateDealMutation.isPending}
              isSaving={isSaving}
              onSubmit={handleSubmit}
              onTeaserUploaded={refetch}
            />
          </div>
        </div>
      </div>

      <CancelDealModal
        dealId={id}
        isOpen={isCancelDealModalOpen}
        onClose={closeCancelDealModal}
      />
    </>
  );
};
